import { Snowflake, Sparkles, BedDouble, UtensilsCrossed, Car, Zap, Speaker, HeartHandshake } from "lucide-react";
import { FadeUp, SectionHeading } from "@/components/Reveal";
import { AMENITIES } from "@/content";

const ICONS = {
  snowflake: Snowflake,
  sparkles: Sparkles,
  bed: BedDouble,
  utensils: UtensilsCrossed,
  car: Car,
  zap: Zap,
  speaker: Speaker,
  heart: HeartHandshake,
};

const PHOTOS = {
  hall: "https://images.pexels.com/photos/12024178/pexels-photo-12024178.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1100",
  entrance:
    "https://images.unsplash.com/photo-1761472606347-bfebc5a3e546?crop=entropy&cs=srgb&fm=jpg&q=85",
  dining:
    "https://images.pexels.com/photos/33914523/pexels-photo-33914523.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=520&w=740",
};

const PhotoCard = ({ src, alt, title, caption, className = "" }) => (
  <div
    className={`group relative overflow-hidden rounded-2xl border border-[#D4AF37]/25 bg-[#2C070B] ${className}`}
  >
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.06]"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-[#120204]/90 via-[#120204]/15 to-transparent" />
    <div className="absolute bottom-0 inset-x-0 p-6">
      <p className="font-serif-lux text-2xl text-[#F3E5AB]">{title}</p>
      <p className="text-[12px] uppercase tracking-[0.24em] text-[#A39791] mt-1.5">{caption}</p>
    </div>
  </div>
);

export const Showcase = () => (
  <section id="venue" data-testid="mandap-showcase-grid" className="relative py-24 lg:py-32 bg-[#180407]">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <SectionHeading
        eyebrow="The Venue"
        title={
          <>
            Crafted for Ceremony,
            <br />
            <span className="italic gold-text">Built for Scale</span>
          </>
        }
        sub="Every corner of Sai Grand Celebrations is composed like a set — from the mandap stage where vows are taken, to the banquet hall where the night unfolds."
      />

      <div className="mt-16 grid md:grid-cols-6 gap-5 auto-rows-[minmax(170px,auto)] grid-flow-dense">
        <FadeUp className="md:col-span-4 md:row-span-2 h-full" delay={0.05}>
          <PhotoCard
            src={PHOTOS.hall}
            alt="Captivating wedding hall adorned with floral decor and sparkling chandeliers"
            title="The Grand Hall"
            caption="Banquet · Chandeliers · Floral Decor"
            className="h-full min-h-[340px] md:min-h-full"
          />
        </FadeUp>

        <FadeUp className="md:col-span-2 h-full" delay={0.12}>
          <div className="glass-card rounded-2xl h-full min-h-[200px] p-7 flex flex-col justify-between">
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">Opening Shortly</span>
            <div>
              <p className="font-serif-lux text-2xl text-[#F3E5AB] leading-snug">
                The doors are almost ready.
              </p>
              <p className="text-sm font-light text-[#A39791] mt-3 leading-relaxed">
                The mandap is in its final stage of preparation. Families who enquire now
                receive first pick of auspicious dates before the public opening.
              </p>
            </div>
          </div>
        </FadeUp>

        <FadeUp className="md:col-span-2 h-full" delay={0.18}>
          <PhotoCard
            src={PHOTOS.dining}
            alt="Royal wedding dining setup with golden accents and floral arrangements"
            title="Royal Dining"
            caption="Feasts served with grandeur"
            className="h-full min-h-[240px]"
          />
        </FadeUp>

        <FadeUp className="md:col-span-2 md:row-span-2 h-full" delay={0.1}>
          <PhotoCard
            src={PHOTOS.entrance}
            alt="Ornate archway entrance with chandeliers and plush seating"
            title="The Grand Entrance"
            caption="Arrive like royalty"
            className="h-full min-h-[300px] md:min-h-full"
          />
        </FadeUp>

        <FadeUp className="md:col-span-4 h-full" delay={0.16}>
          <div className="glass-card rounded-2xl h-full p-7 sm:p-8">
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-5">
              Venue Amenities
            </p>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
              {AMENITIES.slice(0, 4).map((a) => {
                const Icon = ICONS[a.icon];
                return (
                  <div key={a.title} className="flex items-center gap-3.5">
                    <span className="shrink-0 rounded-full border border-[#D4AF37]/40 p-2 text-[#D4AF37]">
                      <Icon size={15} />
                    </span>
                    <p className="text-sm text-[#FAF6F0]/90">{a.title}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </FadeUp>
      </div>

      <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {AMENITIES.slice(4).map((a, i) => {
          const Icon = ICONS[a.icon];
          return (
            <FadeUp key={a.title} delay={0.05 * i} className="h-full">
              <div className="glass-card rounded-2xl h-full p-6 group hover:border-[#D4AF37]/50 transition-[border-color] duration-500">
                <span className="inline-block rounded-full border border-[#D4AF37]/40 p-2.5 text-[#D4AF37] group-hover:bg-[#D4AF37] group-hover:text-[#2C070B] transition-colors duration-500">
                  <Icon size={16} />
                </span>
                <p className="font-serif-lux text-lg text-[#F3E5AB] mt-4">{a.title}</p>
                <p className="text-[13px] font-light text-[#A39791] mt-2 leading-relaxed">{a.text}</p>
              </div>
            </FadeUp>
          );
        })}
      </div>
    </div>
  </section>
);

export default Showcase;
